import { itemsPerPage, pubPathNoTrailingSlash } from "@/config";
import { keysToSnake, toCamel } from "@/helpers/humps";
import type { SearchVideoOptions, TSort } from "@/models/search";

export function searchOptsToQueryString(opts?: SearchVideoOptions): string {
  const params = new URLSearchParams();
  // keysToSnake preserves the input's own type, but `opts` may be undefined
  // at the type level even though every real caller only invokes this with a
  // populated object; this assertion keeps that pre-existing contract intact.
  const snakeOpts = keysToSnake(opts) as Record<string, unknown>;
  for (const param of Object.keys(snakeOpts)) {
    const value = snakeOpts[param];
    const isArr = Array.isArray(value);
    const key =
      isArr && !param?.includes("_sort") && !param?.includes("_filters")
        ? `${param}_filters[]`
        : param;
    if (Array.isArray(value) && value.filter((_) => _).length) {
      value.forEach((v) => params.append(key, v as string));
    } else if (!isArr) {
      const val = (value as string | number | boolean | undefined)?.toString();
      params.set(key, val as string);
    }
  }
  if (opts?.newestSort) params.set("newest_sort", opts?.newestSort);
  if (opts?.difficultySort) params.set("difficulty_sort", opts.difficultySort);
  if (opts?.durationSort) params.set("duration_sort", opts.durationSort);
  const query =
    opts && Object.keys(opts).length > 0 ? decodeURI(params.toString()) : "";
  return query;
}

export function paramstoSearchOpts(
  currentUrl: string | null,
): SearchVideoOptions {
  // Built dynamically from URL query params, so keys/values are strings
  // (or string arrays for repeated "_filter" params) until cast back to
  // SearchVideoOptions at the end.
  const opts: Record<string, string | string[]> = {};
  try {
    if (!currentUrl) {
      return {};
    }
    const url = new URL(
      currentUrl?.includes("http")
        ? currentUrl
        : `http://localhost/${currentUrl}`,
    );
    for (const [key, value] of url.searchParams) {
      if (key?.includes("_sort")) {
        const k = toCamel(key);
        opts[k] = value;
      } else if (key?.includes("_filter")) {
        const filterKey = key.split("_filter")[0];
        const k = toCamel(filterKey);
        const existing = opts[k];
        if (Array.isArray(existing)) {
          existing.push(value);
        } else {
          opts[k] = [value];
        }
      } else if (key === "q") {
        opts[toCamel(key)] = value;
      } else if (key === "type") {
        opts[key] = value;
      }
    }
    return opts as SearchVideoOptions;
  } catch (_err) {
    return {};
  }
}

export function shouldSearch(opts: SearchVideoOptions): boolean {
  // Should submit query when there is a q param or other filters besides default are set
  const isRecentlyAdded =
    typeof window !== "undefined" &&
    [
      `${pubPathNoTrailingSlash}/routines/newly-added`,
      `${pubPathNoTrailingSlash}/routines`,
      `${pubPathNoTrailingSlash}/search`,
    ].includes(window.location.pathname);
  return (
    "q" in opts ||
    Object.keys(opts).length > Object.keys(defaultSearchOpts).length ||
    isRecentlyAdded
  );
}

const defaultSearchOpts: Record<string, number | TSort> = {
  from: 0,
  size: itemsPerPage,
};

export function addDefaultSearchOpts(
  opts?: SearchVideoOptions | null,
): SearchVideoOptions {
  const defaultOps = { ...defaultSearchOpts };

  // If opts is null or undefined, return default options
  if (!opts) {
    return defaultOps;
  }

  // Filter out properties with undefined values from opts
  const filteredOpts = Object.fromEntries(
    Object.entries(opts).filter(([_, value]) => value !== undefined),
  );

  // Delete type property if it exists in filteredOpts
  if (filteredOpts.type) {
    delete filteredOpts.type;
  }

  // Apply sorting logic based on filteredOpts
  if (filteredOpts.relevancySort || filteredOpts.durationSort) {
    delete defaultOps.newestSort;
  } else if (filteredOpts.newestSort && !filteredOpts.relevancySort) {
    defaultOps.newestSort = "desc";
    delete defaultOps.relevancySort;
  } else if (!filteredOpts.relevancySort) {
    delete defaultOps.newestSort;
    defaultOps.relevancySort = "desc";
  }

  // Merge defaultOps with filteredOpts
  return { ...defaultOps, ...filteredOpts };
}

const defaultSearchOptsMember = {
  from: 0,
  size: itemsPerPage,
  newestSort: true,
};

export function addDefaultSearchOptsMember(
  opts?: SearchVideoOptions | null,
): SearchVideoOptions {
  return { ...defaultSearchOptsMember, ...opts };
}
